/**
 * @fileoverview useElementAnimation — the single hook shared components use to
 * consume the screen animation plan. Returns className/style/data props to
 * merge onto the component's existing root element.
 * @module screen-view-v2/animations/useElementAnimation
 *
 * Lookup key = the component's `editPath` minus the `content.` prefix, which
 * equals the schema dot-path used by `elementAnimations[].elementRef`.
 * Options items fall back to the `options.item` template entry
 * (OptionsList.itemAnimation) when no exact per-item entry exists.
 *
 * Mount-driven triggers handled here: onAppear, afterDelay (CSS delay),
 * onScreenEnter — ALL held PAUSED at their `from` state (`--sv2-anim-play:
 * paused`) until the screen transition settles, so entrances never play —
 * and elements never flash their final layout — while the screen is still
 * sliding in behind the transition container. Loops attach at settle instead
 * (paused 0% could be an off-base pose). Event-driven triggers
 * (onSelectionChange / onTap) are handled by the companion hooks in
 * useEventAnimation.ts.
 */

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useScreenAnimationContext } from './AnimationContext';
import { buildElementAnimationSpec } from './elementAnimationSpec';
import { OPTIONS_ITEM_REF, PAYWALL_PRODUCT_ITEM_REF, type PlanEntry } from './resolveScreenAnimationPlan';
import { ssrEntranceProps } from './ssrEntrance';

export interface ElementAnimationProps {
  className?: string;
  style?: CSSProperties;
  'data-anim'?: string;
}

const EMPTY: ElementAnimationProps = {};

// Warn once per ref — this hook runs on every render of animated screens
const warnedUnconsumedEventRefs = new Set<string>();

export function normalizeElementRef(editPath: string): string {
  return editPath.startsWith('content.') ? editPath.slice('content.'.length) : editPath;
}

/** Exact entry, else the item template (options / paywall products) for per-item paths */
function lookupEntry(
  plan: Map<string, PlanEntry>,
  ref: string,
): PlanEntry | undefined {
  const exact = plan.get(ref);
  if (exact) return exact;
  if (/^options\.items\.\d+$/.test(ref)) {
    return plan.get(OPTIONS_ITEM_REF);
  }
  if (/^paywall\.products\.items\.\d+$/.test(ref)) {
    return plan.get(PAYWALL_PRODUCT_ITEM_REF);
  }
  return undefined;
}

export interface UseElementAnimationOptions {
  /** 0-based display position — feeds stagger math (options items) */
  staggerIndex?: number;
}

export function useElementAnimation(
  editPath?: string,
  options?: UseElementAnimationOptions,
): ElementAnimationProps {
  const ctx = useScreenAnimationContext();
  const staggerIndex = options?.staggerIndex;

  return useMemo<ElementAnimationProps>(() => {
    if (!ctx || !ctx.enabled || !editPath) return EMPTY;
    const ref = normalizeElementRef(editPath);
    const entry = lookupEntry(ctx.plan, ref);
    if (!entry) return EMPTY;

    const spec = buildElementAnimationSpec(entry.animation, {
      reducedMotion: ctx.reducedMotion,
      staggerIndex: staggerIndex ?? entry.staggerIndex,
      stagger: entry.stagger,
      defaultTrigger: entry.defaultTrigger,
      forceLoop: entry.forceLoop,
    });
    if (!spec) return EMPTY;

    // Event-driven triggers are applied by useEventAnimation hooks, not here.
    // Those hooks only consume `#`-suffixed selection refs (options items /
    // product offers) and Button TapTargetStyle fields — a plain plan entry
    // resolving to an event trigger here has no consumer, so surface that
    // instead of silently never playing.
    if (spec.trigger === 'onSelectionChange' || spec.trigger === 'onTap') {
      if (!warnedUnconsumedEventRefs.has(ref)) {
        warnedUnconsumedEventRefs.add(ref);
        console.warn(
          `[ElementAnimation] "${ref}" uses trigger "${spec.trigger}" which has no consumer on this element — `
          + 'use options selection/deselection animations or Button tapAnimation instead',
        );
      }
      return EMPTY;
    }
    // FSE (B-wait): the published render paints the first screen's ENTRANCE running from first paint — no
    // paused-hold (the static page has no transition to wait for), a marker for the <html> gate and the LCP-safe
    // fade floor. Everything else (loops, emphasis) stays client-only.
    if (ctx.ssrEntranceOnly) {
      if (spec.category !== 'entrance') return EMPTY;
      return { ...ssrEntranceProps(spec), 'data-anim': ref };
    }
    // The static (SSR) page already painted this screen in its final state:
    // an entrance here would drop the painted content to its `from` state and
    // play it again when the client replaces the page (AMV-AC-036). Takes
    // precedence over the paused-hold below. Only the entrance category:
    // emphasis (fill none, infinite repeat included) and loops never hide
    // content and never played in the class-free static page.
    if (ctx.skipMountEntrances && spec.category === 'entrance') return EMPTY;
    // Hold ALL mount-driven triggers until the screen transition settles —
    // entrances (onAppear included: it's the default for theme-generated
    // screenAnimation) must not play while the screen is still sliding in
    // behind the transition container, and "delay from screen appear" must
    // not tick away early. screenEntered defaults to true whenever no
    // transition container is involved (editor preview, articles, initial
    // mounts settle immediately), so this only defers during a live swap.
    //
    // The hold is PAUSED, not class-less: the class + `--sv2-anim-play:
    // paused` ship from the element's first painted frame, so `backwards`
    // fill pins it at the `from` keyframe (hidden/offset) while the screen
    // slides in — it must never paint in its final state before the entrance
    // plays. Settle flips the var to its `running` default, which resumes the
    // animation AND starts the delay countdown (stagger counts from settle).
    // Loops are exempt (today's attach-at-settle): paused at their 0%
    // keyframe they could freeze in an off-base pose, and their `fill: none`
    // means late attach causes no visual snap.
    if (!ctx.screenEntered && spec.category === 'loop') {
      return EMPTY;
    }

    return {
      className: spec.className,
      style: (ctx.screenEntered
        ? spec.vars
        : { ...spec.vars, '--sv2-anim-play': 'paused' }) as CSSProperties,
      'data-anim': ref,
    };
  }, [ctx, editPath, staggerIndex]);
}

/** Merge helper: append the animation class to an existing className string */
export function mergeAnimationClassName(
  base: string | undefined,
  anim: ElementAnimationProps,
): string | undefined {
  if (!anim.className) return base;
  return base ? `${base} ${anim.className}` : anim.className;
}
